import { useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import CheckboxElement from 'components/elements/CheckboxElement';
import ElementEditor from 'components/elements/ElementEditor';
import GuidanceTextElement from 'components/elements/GuidanceTextElement';
import LookupElement from 'components/elements/LookupElement';
import RadioElement from 'components/elements/RadioElement';
import RichTextElement from 'components/elements/RichTextElement';
import SectionHeaderElement from 'components/elements/SectionHeaderElement';
import TextAreaElement from 'components/elements/TextAreaElement';
import TextBoxElement from 'components/elements/TextBoxElement';
import { useAppDispatch } from 'hooks/redux';
import { cloneDeep } from 'lodash';
import { showErrorMessage } from 'store/errorModalSlice';
import { saveElementValuesAsync, updateElement } from 'store/rfiSlice';
import { ElementMode, fieldTypes, RfiElementSelectionList } from 'types/RfiTypes';
import RfiElement from '../../types/RfiElement';
import AttachmentElement from './AttachmentElement';
import DatePickerElement from './DatePickerElement';
import DateTimeElement from './DateTimeElement';
import DropDownElement from './DropDownElement';
import ElementCopier from './ElementCopier';
import ElementMover from './ElementMover';
import SelectionListElement from './SelectionListElement';
import TimePickerElement from './TimePickerElement';

export interface IElementFactoryProps {
  element: RfiElement;
  designMode?: boolean;
  readOnly?: boolean;
  editElementMode?: ElementMode;
  questionNumber?: number;
  userRole?: string;
  overrideRequired?: boolean;

  rfiId?: number;
  modifyElements?: (modifyAction: () => Promise<void>) => Promise<void>;
  currentSection?: string;
  sectionElements?: Array<RfiElementSelectionList>;
  rerender?: boolean;
  defaultValue?: string;
  showValue?: boolean;
}

const ElementFactory = (props: IElementFactoryProps) => {
  // Hooks
  const dispatch = useAppDispatch();
  // const rfi = useAppSelector(selectRfi);

  // State
  const [propState, setPropState] = useState(props);
  const [element, setElement] = useState<RfiElement>(() =>
    cloneDeep(propState.element)
  );

  // Effects
  useEffect(() => {
    setPropState(props);
  }, [props]);

  // Handlers
  const handleUpdate = async (): Promise<void> => {
    dispatch(saveElementValuesAsync());

    await axios
      .put("/api/v1/elements/rfi", element)
      .then((res: any) => {
        //console.log("Done Updating");
        element.options = res.data.options;
        dispatch(
          updateElement({
            element: element,
            section: props.currentSection ?? "",
          })
        );
        return res.data;
      })
      .catch((error: Error | AxiosError) => {
        console.log(error);
        dispatch(
          showErrorMessage(`There was an error updating element - ${error}`)
        );
      });
  };

  // Edit/Move/Copy Tabs

  if (propState.editElementMode === "edit")
    return (
      <div className="my-3">
        <ElementEditor
          element={element}
          overrideRequired={props.overrideRequired}
          modifyElement={setElement}
          userRole={props.userRole}
          handleUpdate={handleUpdate}
          modifyElements={props.modifyElements}
        />
      </div>
    );

  if (propState.editElementMode === "copy")
    return (
      <div className="my-3">
        <ElementCopier
          rfiId={propState.rfiId}
          element={element}
          modifyElement={setElement}
          currentSection={propState.currentSection}
          targetElementList={propState?.sectionElements ?? ([] as [])}
          userRole={propState.userRole}
          modifyElements={props.modifyElements}
          handleUpdate={handleUpdate}
        />
      </div>
    );

  if (propState.editElementMode === "move")
    return (
      <div className="my-3">
        <ElementMover
          element={element}
          modifyElement={setElement}
          currentSection={propState.currentSection}
          targetElementList={
            propState?.sectionElements ?? ([] as Array<RfiElementSelectionList>)
          }
          modifyElements={props.modifyElements}
          handleUpdate={handleUpdate}
        />
      </div>
    );

  // Factory
  switch (props?.element?.fieldType) {
    case fieldTypes.CHECKBOX:
      return <CheckboxElement {...propState} />;
    case fieldTypes.RADIOGROUP:
      return <RadioElement {...propState} />;
    case fieldTypes.DROPDOWN:
      return <DropDownElement {...propState} />;
    case fieldTypes.SECTIONHEADER:
      return <SectionHeaderElement {...propState} />;
    case fieldTypes.GUIDANCETEXT:
      return <GuidanceTextElement {...propState} />;
    case fieldTypes.TEXTFIELD:
      return <TextBoxElement {...propState} />;
    case fieldTypes.DATETIME:
      return <DateTimeElement {...propState} />;
    case fieldTypes.TEXTAREA:
      return <TextAreaElement {...propState} />;
    case fieldTypes.RICHTEXT:
      return <RichTextElement {...propState} />;
    case fieldTypes.ATTACHMENT:
      return <AttachmentElement {...propState} />;
    case fieldTypes.DATEPICKER:
      return <DatePickerElement {...propState} />;
    case fieldTypes.TIMEPICKER:
      return <TimePickerElement {...propState} />;
    case fieldTypes.LOOKUP:
      return (
        <LookupElement
          endpoint={props?.element?.endpoint}
          isMulti={false}
          {...propState}
        />
      );
    case fieldTypes.LOOKUPMULTIPLE:
      return (
        <LookupElement
          endpoint={props?.element?.endpoint}
          isMulti={true}
          {...propState}
        />
      );

    case fieldTypes.SELECT:
      return (
        <SelectionListElement
          endpoint={props?.element?.endpoint}
          isMulti={false}
          {...propState}
        />
      );
    case fieldTypes.SELECTMULTIPLE:
      return (
        <SelectionListElement
          endpoint={props?.element?.endpoint}
          isMulti={true}
          {...propState}
        />
      );
  }

  return <div>Unknown field type - {props?.element?.fieldType}</div>;
};

export default ElementFactory;
